import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

// Convert date to YYYY-MM-DD format
function toISODate(dateStr: string): string {
  if (!dateStr) return '';
  
  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Handle DD/MM/YYYY format
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
  }
  
  // Try parsing as Date object
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date.toISOString().slice(0, 10);
  }
  
  return dateStr;
}

export default function SettingsPage() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [emailUpdateMessage, setEmailUpdateMessage] = useState('');

  // Personal Information State
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',
    bio: '',
  });

  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [personalInfoError, setPersonalInfoError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSavingPersonal, setIsSavingPersonal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showNotificationSection, setShowNotificationSection] = useState(false);
  const [showPrivacySection, setShowPrivacySection] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    bookingEmails: true,
    messageEmails: true,
    reminderEmails: true,
    pushMessages: false,
  });
  const [passwordError, setPasswordError] = useState('');

  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    bookingUpdates: true,
    messageAlerts: true,
    marketingEmails: false,
    twoFactorAuth: false,
    publicProfile: true,
    showEmail: false,
    showPhone: false,
  });

  const [verificationStatus, setVerificationStatus] = useState<'not_verified' | 'pending' | 'verified' | 'rejected'>('not_verified');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState<{
    idFront?: string;
    idBack?: string;
    proofOfAddress?: string;
    selfie?: string;
  }>({});

  const profileComplete = !!(
    profile?.phone &&
    profile?.date_of_birth &&
    profile?.street_address &&
    profile?.suburb &&
    profile?.state &&
    profile?.postcode
  );

  const [consentChecked, setConsentChecked] = useState(false);

  // Load profile data on mount and when profile changes
  useEffect(() => {
    if (profile) {
      const formData = {
        fullName: profile.full_name || '',
        email: user?.email || '',
        phone: profile.phone || '',
        dateOfBirth: profile.date_of_birth || '',
        address: profile.street_address || '',
        suburb: profile.suburb || '',
        state: profile.state || '',
        postcode: profile.postcode || '',
        country: profile.country || '',
        bio: profile.bio || '',
      };

      setPersonalInfo(formData);

      // Set verification status
      if (profile.is_verified) {
        setVerificationStatus('verified');
      } else if (profile.verification_status) {
        setVerificationStatus(profile.verification_status as any);
      }

      if (profile.verification_notes) {
        setVerificationNotes(profile.verification_notes);
      }
    } else if (user) {
      setPersonalInfo(prev => ({
        ...prev,
        email: user.email || '',
      }));
    } else {
      setPersonalInfo({
        fullName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        address: '',
        suburb: '',
        state: '',
        postcode: '',
        country: '',
        bio: '',
      });
    }
  }, [profile, user]);

  const handlePersonalInfoChange = (field: string, value: string) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSavePersonalInfo = async () => {
    if (!user) {
      setPersonalInfoError('You must be logged in to update your profile');
      return;
    }

    // Validate all required fields
    const requiredFields: Record<string, string> = {
      fullName: 'Full name',
      phone: 'Phone number',
      dateOfBirth: 'Date of birth',
      address: 'Street address',
      suburb: 'Suburb',
      state: 'State',
      postcode: 'Postcode',
      country: 'Country',
    };
    const missingFields = Object.entries(requiredFields)
      .filter(([key]) => !personalInfo[key as keyof typeof personalInfo]?.toString().trim())
      .map(([, label]) => label);
    if (missingFields.length > 0) {
      setPersonalInfoError(`Please complete the following fields: ${missingFields.join(', ')}`);
      const newFieldErrors: Record<string, boolean> = {};
      Object.entries(requiredFields).forEach(([key]) => {
        if (!personalInfo[key as keyof typeof personalInfo]?.toString().trim()) {
          newFieldErrors[key] = true;
        }
      });
      setFieldErrors(newFieldErrors);
      return;
    }

    setIsSavingPersonal(true);
    setPersonalInfoError('');
    setFieldErrors({});

    try {
      // STEP 1: Validate session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('Your session has expired. Please log in again.');
      }
      
      if (!session) {
        throw new Error('Your session has expired. Please log in again.');
      }

      // STEP 2: Prepare update data
      const updateData: any = {
        full_name: personalInfo.fullName || null,
        phone: personalInfo.phone || null,
        date_of_birth: personalInfo.dateOfBirth || null,
        street_address: personalInfo.address || null,
        suburb: personalInfo.suburb || null,
        state: personalInfo.state || null,
        postcode: personalInfo.postcode || null,
        country: personalInfo.country || null,
        bio: personalInfo.bio || null,
      };

      // STEP 3: Try direct database update
      const { data: directData, error: directError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select()
        .single();

      // Check if direct update succeeded
      if (directError) {
        // STEP 4: Fallback to Edge Function
        const edgeFunctionUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-profile`;
        
        const response = await fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.error || responseData.details || `Server error: ${response.status}`);
        }

        if (!responseData.success) {
          throw new Error(responseData.error || responseData.details || 'Failed to update profile');
        }
        
      } else if (!directData) {
        throw new Error('Profile not found. Please contact support.');
      }

      // STEP 5: Refresh profile
      await refreshProfile();

      // STEP 6: Show success
      setIsEditingPersonal(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

    } catch (error: any) {
      // Show user-friendly error message
      let errorMessage = 'Failed to update profile. ';
      
      if (error.message.includes('session') || error.message.includes('expired')) {
        errorMessage += 'Your session has expired. Please log out and log back in.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage += 'Network error. Please check your internet connection.';
      } else if (error.message.includes('not found')) {
        errorMessage += 'Profile not found. Please contact support.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      setPersonalInfoError(errorMessage);
    } finally {
      setIsSavingPersonal(false);
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleChangePassword = async () => {
    setPasswordError('');

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswordSection(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

    } catch (error: any) {
      setPasswordError(error.message || 'Failed to update password');
    }
  };

  const handleDocumentUpload = async (type: 'idFront' | 'idBack' | 'proofOfAddress' | 'selfie', file: File) => {
    setVerificationError('');

    if (isEditingPersonal) {
      const confirm = window.confirm('You have unsaved changes in your Personal Information. Please save your details before uploading documents. Click OK to go back and save.');
      if (confirm) return;
    }

    setUploadingDoc(true);

    try {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setVerificationError('File size must be less than 5MB');
        setUploadingDoc(false);
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setVerificationError('Only JPG, PNG, WEBP, and PDF files are allowed');
        setUploadingDoc(false);
        return;
      }

      if (!user) throw new Error('Not authenticated');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${type}_${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('verification-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      setUploadedDocs(prev => ({ ...prev, [type]: data.path }));

    } catch (error: any) {
      setVerificationError(error.message || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!confirm('Are you sure you want to request immediate deletion of your verification documents? This action cannot be undone.')) {
      return;
    }

    try {
      if (!user) return;

      await supabase.from('verification_audit_log').insert({
        user_id: user.id,
        action: 'deletion_requested',
        details: {
          requested_at: new Date().toISOString(),
        },
        timestamp: new Date().toISOString(),
      });

      alert('Deletion request submitted. Your documents will be removed within 24 hours and you will receive a confirmation email.');
    } catch (error) {
      alert('Failed to submit deletion request. Please contact support.');
    }
  };

  const handleSubmitVerification = async () => {
    setVerificationError('');

    if (!user) {
      setVerificationError('You must be logged in to submit verification.');
      return;
    }

    if (!consentChecked) {
      setVerificationError('You must agree to the document retention policy before submitting.');
      return;
    }

    if (Object.keys(uploadedDocs).length === 0) {
      setVerificationError('Please upload at least one document before submitting.');
      return;
    }

    setUploadingDoc(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          verification_status: 'pending',
          verification_documents: uploadedDocs,
        })
        .eq('id', user.id);

      if (error) throw error;

      await supabase.from('verification_audit_log').insert({
        user_id: user.id,
        action: 'verification_submitted',
        details: {
          submitted_at: new Date().toISOString(),
          documents: Object.keys(uploadedDocs),
        },
        timestamp: new Date().toISOString(),
      });

      setVerificationStatus('pending');
      await refreshProfile();
    } catch (error: any) {
      setVerificationError(error.message || 'Failed to submit verification. Please try again.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newEmail || newEmail === user?.email) {
      setEmailUpdateMessage('Please enter a different email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailUpdateMessage('Please enter a valid email address');
      return;
    }

    setIsUpdatingEmail(true);
    setEmailUpdateMessage('');

    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) throw error;

      setEmailUpdateMessage('Verification email sent! Please check your new email address and click the confirmation link.');
      setTimeout(() => {
        setIsEmailModalOpen(false);
        setNewEmail('');
        setEmailUpdateMessage('');
      }, 3000);
    } catch (error: any) {
      setEmailUpdateMessage(error.message || 'Failed to update email. Please try again.');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="font-medium">Back</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
              <p className="text-green-800 font-medium">Changes saved successfully!</p>
            </div>
          )}

          {/* Action Required Banner */}
          {!profileComplete && !profile?.is_verified && (
            <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-500 text-2xl mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-red-700 font-bold text-base">Action Required</p>
                <p className="text-red-600 text-sm mt-1">You must complete <strong>all fields</strong> in Personal Information and click <strong>Save Changes</strong> before you can begin identity verification.</p>
              </div>
            </div>
          )}

          {/* Verification Status Card */}
          <div className={`bg-white rounded-xl shadow-md p-6 mb-8 ${!profileComplete ? 'opacity-60 pointer-events-none' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Verification Status</h2>
              {profile?.is_verified || profile?.verification_status === 'verified' ? (
                <div className="flex items-center px-4 py-2 bg-green-100 rounded-full">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl mr-2"></i>
                  <span className="text-green-800 font-semibold">Verified</span>
                </div>
              ) : profile?.verification_status === 'pending' ? (
                <div className="flex items-center px-4 py-2 bg-orange-100 rounded-full">
                  <i className="ri-time-line text-orange-600 text-xl mr-2"></i>
                  <span className="text-orange-800 font-semibold">Pending</span>
                </div>
              ) : profile?.verification_status === 'rejected' ? (
                <div className="flex items-center px-4 py-2 bg-red-100 rounded-full">
                  <i className="ri-close-circle-fill text-red-600 text-xl mr-2"></i>
                  <span className="text-red-800 font-semibold">Rejected</span>
                </div>
              ) : (
                <div className="flex items-center px-4 py-2 bg-red-100 rounded-full">
                  <i className="ri-error-warning-fill text-red-600 text-xl mr-2"></i>
                  <span className="text-red-800 font-semibold">Not Verified</span>
                </div>
              )}
            </div>
            
            {(!profile?.is_verified && profile?.verification_status !== 'verified' && profile?.verification_status !== 'pending') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-900 mb-4">
                  <strong>To list a space you must be verified.</strong> Verification helps build trust with renters and is required to list spaces on Stashly.
                </p>
                <button
                  onClick={() => {
                    document.getElementById('verification-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Start Verification
                </button>
              </div>
            )}

            {profile?.verification_status === 'pending' && !profile?.is_verified && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-orange-900 mb-4">
                  <strong>Your verification is in progress.</strong> If you didn't complete the verification process, you can restart it below.
                </p>
                <button
                  onClick={() => {
                    document.getElementById('verification-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Restart Verification
                </button>
              </div>
            )}

            {profile?.verification_status === 'rejected' && profile?.verification_notes && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-900 mb-2">
                  <strong>Verification Rejected:</strong>
                </p>
                <p className="text-red-800">{profile.verification_notes}</p>
                <button
                  onClick={() => {
                    document.getElementById('verification-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="mt-4 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Resubmit Documents
                </button>
              </div>
            )}
          </div>

          {/* Personal Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
              {!isEditingPersonal && (
                <button
                  onClick={() => setIsEditingPersonal(true)}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Edit Profile
                </button>
              )}
            </div>

            {personalInfoError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line text-xl"></i>
                <span>{personalInfoError}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={personalInfo.fullName}
                  onChange={(e) => handlePersonalInfoChange('fullName', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-2 border ${fieldErrors['fullName'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 text-sm`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col md:flex-row gap-2 md:gap-3">
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm"
                  />
                  <button
                    onClick={() => setIsEmailModalOpen(true)}
                    className="w-full md:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    Change Email
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={personalInfo.phone}
                  onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-3 border ${fieldErrors['phone'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm`}
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={personalInfo.dateOfBirth}
                  onChange={(e) => handlePersonalInfoChange('dateOfBirth', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-3 border ${fieldErrors['dateOfBirth'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm`}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={personalInfo.address}
                  onChange={(e) => handlePersonalInfoChange('address', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-3 border ${fieldErrors['address'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm`}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Suburb
                </label>
                <input
                  type="text"
                  name="suburb"
                  value={personalInfo.suburb}
                  onChange={(e) => handlePersonalInfoChange('suburb', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-3 border ${fieldErrors['suburb'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={personalInfo.state}
                  onChange={(e) => handlePersonalInfoChange('state', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-3 border ${fieldErrors['state'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Postcode
                </label>
                <input
                  type="text"
                  name="postcode"
                  value={personalInfo.postcode}
                  onChange={(e) => handlePersonalInfoChange('postcode', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-3 border ${fieldErrors['postcode'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm`}
                  placeholder="Enter postcode"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={personalInfo.country}
                  onChange={(e) => handlePersonalInfoChange('country', e.target.value)}
                  disabled={!isEditingPersonal}
                  className={`w-full px-4 py-3 border ${fieldErrors['country'] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm`}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  name="bio"
                  value={personalInfo.bio}
                  onChange={(e) => handlePersonalInfoChange('bio', e.target.value)}
                  disabled={!isEditingPersonal}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 text-sm resize-none"
                  placeholder="Tell us a bit about yourself..."
                />
              </div>

              {isEditingPersonal && (
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setIsEditingPersonal(false);
                      setFieldErrors({});
                      setPersonalInfoError('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePersonalInfo}
                    disabled={isSavingPersonal}
                    className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {isSavingPersonal ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Password Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Password</h2>
                <p className="text-sm text-gray-600 mt-1">Change your password to keep your account secure</p>
              </div>
              {!showPasswordSection && (
                <button
                  onClick={() => setShowPasswordSection(true)}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Change Password
                </button>
              )}
            </div>
            {showPasswordSection && (
              <div className="p-6">
                {passwordError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    <p className="text-red-800 text-sm">{passwordError}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                      placeholder="Enter current password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                      placeholder="Enter new password (min 8 characters)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordError('');
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                      });
                    }}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    className="px-6 py-2.5 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notification Preferences */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Notification Preferences</h2>
                <p className="text-sm text-gray-600 mt-1">Choose how you want to receive updates and alerts</p>
              </div>
              {!showNotificationSection && (
                <button
                  onClick={() => setShowNotificationSection(true)}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Manage Preferences
                </button>
              )}
            </div>
            {showNotificationSection && (
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="font-semibold text-gray-900">Email notifications for new bookings</p>
                      <p className="text-sm text-gray-500">Receive an email when a new booking is made</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.bookingEmails}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, bookingEmails: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#14B8A6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="font-semibold text-gray-900">Email notifications for new messages</p>
                      <p className="text-sm text-gray-500">Receive an email when you get a new message</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.messageEmails}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, messageEmails: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#14B8A6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="font-semibold text-gray-900">Email notifications for booking reminders</p>
                      <p className="text-sm text-gray-500">Receive reminder emails before upcoming bookings</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.reminderEmails}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, reminderEmails: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#14B8A6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-semibold text-gray-900">Push notifications for new messages</p>
                      <p className="text-sm text-gray-500">Receive push notifications for new messages</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.pushMessages}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, pushMessages: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#14B8A6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                    </label>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setShowNotificationSection(false)}
                    className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Privacy Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Privacy &amp; Security</h2>
                <p className="text-sm text-gray-600 mt-1">Control your privacy and security settings</p>
              </div>
              {!showPrivacySection && (
                <button
                  onClick={() => setShowPrivacySection(true)}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Manage Settings
                </button>
              )}
            </div>
            {showPrivacySection && (
            <div className="p-6">
              <div className="space-y-6">


                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Public Profile</h3>
                    <p className="text-xs text-gray-600 mt-1">Make your profile visible to other users</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.publicProfile}
                      onChange={(e) => setSettings(prev => ({ ...prev, publicProfile: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#14B8A6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Show Email Address</h3>
                    <p className="text-xs text-gray-600 mt-1">Display your email on your public profile</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showEmail}
                      onChange={(e) => setSettings(prev => ({ ...prev, showEmail: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#14B8A6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Show Phone Number</h3>
                    <p className="text-xs text-gray-600 mt-1">Display your phone number on your public profile</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showPhone}
                      onChange={(e) => setSettings(prev => ({ ...prev, showPhone: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#14B8A6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                  </label>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowPrivacySection(false)}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer"
                >
                  Done
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Identity Verification Section */}
          <div id="verification-section" className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-8 mb-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Identity Verification</h2>
            </div>

            {verificationError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-600"></i>
                <p className="text-red-800 text-sm">{verificationError}</p>
              </div>
            )}

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Current Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Verification Status:</span>
                    <span className={`font-semibold ${
                        verificationStatus === 'verified' ? 'text-green-600' :
                        verificationStatus === 'pending' ? 'text-orange-500' :
                        'text-red-600'
                      }`}>
                      {verificationStatus === 'verified' ? 'Verified' :
                       verificationStatus === 'pending' ? 'Pending' :
                       verificationStatus === 'rejected' ? 'Rejected' :
                       'Not Verified'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Verify with Stripe Identity</h3>
                <p className="text-sm text-gray-600 mb-4">Stashly uses Stripe Identity to securely verify your identity. You will need a government-issued photo ID and will be asked to take a selfie. The process takes less than 2 minutes.</p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>✓ Driving licence, passport or ID card accepted</li>
                  <li>✓ Selfie match required</li>
                  <li>✓ Phone number verification included</li>
                  <li>✓ Secure and private — powered by Stripe</li>
                </ul>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={async () => {
                    if (!user) return;
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/stripe-identity-verify`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                        body: JSON.stringify({ userId: user.id, returnUrl: 'https://www.stashly.com.au/settings?verified=true' })
                      });
                      const data = await response.json();
                      if (data.url) { window.location.href = data.url; }
                      else { alert('Could not start verification. Please try again.'); }
                    } catch (err) {
                      alert('Could not start verification. Please try again.');
                    }
                  }}
                  disabled={verificationStatus === 'verified'}
                  className={`px-8 py-3 font-semibold rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                    verificationStatus === 'verified'
                      ? 'bg-green-500 text-white disabled:opacity-70'
                      : verificationStatus === 'pending'
                      ? 'bg-orange-500 text-white disabled:opacity-70'
                      : 'bg-[#14B8A6] text-white hover:bg-[#0F9B8E] disabled:opacity-50'
                  }`}
                >
                  {uploadingDoc ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting...
                    </>
                  ) : verificationStatus === 'verified' ? (
                    <>
                      <i className="ri-checkbox-circle-fill"></i>
                      Verified
                    </>
                  ) : verificationStatus === 'pending' ? (
                    <>
                      <i className="ri-time-line"></i>
                      Verification Pending
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      Submit Verification
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Email Change Modal */}
          {isEmailModalOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Change Email Address</h3>
                  <button
                    onClick={() => {
                      setIsEmailModalOpen(false);
                      setNewEmail('');
                      setEmailUpdateMessage('');
                    }}
                    className="text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                <form onSubmit={handleEmailChange}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Email
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter your new email"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {emailUpdateMessage && (
                    <div className={`mb-4 p-3 rounded-lg text-sm ${
                      emailUpdateMessage.includes('sent') 
                        ? 'bg-green-50 text-green-700' 
                        : 'bg-red-50 text-red-700'
                    }`}>
                      {emailUpdateMessage}
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex gap-2">
                      <i className="ri-information-line text-blue-600 text-lg flex-shrink-0"></i>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Email Verification Required</p>
                        <p>We'll send a verification link to your new email address. You must click the link to complete the email change.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEmailModalOpen(false);
                        setNewEmail('');
                        setEmailUpdateMessage('');
                      }}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isUpdatingEmail}
                      className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {isUpdatingEmail ? 'Sending...' : 'Send Verification Email'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
